/**
 * Skeleton Loading State for ScenarioEditor
 *
 * Shows a content-shaped placeholder matching the final layout
 * to avoid layout shift and provide better UX during loading.
 *
 * @module components/ux/SkeletonScenarioEditor
 */

import React from 'react';
import { Box, Container } from '@mui/material';
import { Skeleton, SkeletonFormField } from './Skeleton';

/**
 * Skeleton for the ScenarioEditor page
 * Matches the structure: header, tabs, and content area
 */
export function SkeletonScenarioEditor() {
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Breadcrumb */}
            <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" className="vx-skeleton-text medium" />
            </Box>

            {/* Header with title and actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="heading" sx={{ width: 200, height: 40 }} />
                    <Skeleton variant="circular" width={32} height={32} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
                    <Skeleton variant="button" />
                </Box>
            </Box>

            {/* Tabs skeleton */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, borderBottom: '1px solid var(--border-subtle)', pb: 1 }}>
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            width={80 + Math.random() * 40}
                            height={36}
                            sx={{ borderRadius: 1 }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Content area skeleton - resembles a form */}
            <Box
                className="vx-skeleton"
                sx={{
                    p: 3,
                    borderRadius: 'var(--border-radius-md)',
                    minHeight: 400,
                }}
            >
                {/* Section title */}
                <Skeleton variant="heading" sx={{ width: '30%', mb: 3 }} />

                {/* Form fields in a grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
                    <SkeletonFormField />
                    <SkeletonFormField />
                    <SkeletonFormField />
                    <SkeletonFormField />
                </Box>

                {/* Another section */}
                <Skeleton variant="heading" sx={{ width: '25%', mt: 4, mb: 3 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
                    <SkeletonFormField />
                    <SkeletonFormField />
                </Box>

                {/* Action area */}
                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Skeleton variant="button" width={120} />
                    <Skeleton variant="button" width={100} />
                </Box>
            </Box>
        </Container>
    );
}

/**
 * Skeleton for a single tab content panel
 * Use when loading tab-specific content
 */
export function SkeletonTabContent({ variant = 'form' }) {
    if (variant === 'results') {
        return (
            <Box sx={{ p: 2 }}>
                {/* Metrics cards row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 4 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Box
                            key={i}
                            className="vx-skeleton"
                            sx={{
                                p: 2,
                                borderRadius: 'var(--border-radius-md)',
                                minHeight: 100,
                            }}
                        >
                            <Skeleton variant="text" className="vx-skeleton-text short" sx={{ mb: 1 }} />
                            <Skeleton variant="heading" sx={{ width: '60%', height: '2em' }} />
                        </Box>
                    ))}
                </Box>

                {/* Chart area */}
                <Box
                    className="vx-skeleton"
                    sx={{
                        height: 300,
                        borderRadius: 'var(--border-radius-md)',
                        mb: 3,
                    }}
                />

                {/* Table area */}
                <Box className="vx-skeleton" sx={{ borderRadius: 'var(--border-radius-md)', p: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                gap: 2,
                                py: 1.5,
                                borderBottom: i < 5 ? '1px solid rgba(128,128,128,0.1)' : 'none',
                            }}
                        >
                            <Skeleton variant="text" sx={{ flex: 2 }} />
                            <Skeleton variant="text" sx={{ flex: 1 }} />
                            <Skeleton variant="text" sx={{ flex: 1 }} />
                            <Skeleton variant="text" sx={{ flex: 1 }} />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    // Default: form variant
    return (
        <Box sx={{ p: 2 }}>
            <Skeleton variant="heading" sx={{ width: '30%', mb: 3 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3 }}>
                <SkeletonFormField />
                <SkeletonFormField />
                <SkeletonFormField />
                <SkeletonFormField />
                <SkeletonFormField />
                <SkeletonFormField />
            </Box>
        </Box>
    );
}

export default SkeletonScenarioEditor;
